import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  acceptWorkspaceInvite,
  isAdminLoggedIn,
  previewWorkspaceInvite,
  setAdminSession,
} from "../api/adminClient";
import { ROUTES } from "./routes";
import "./login.css";

export default function WorkspaceJoinPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isAdminLoggedIn()) {
      navigate(ROUTES.dashboard, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    previewWorkspaceInvite(token)
      .then((res) => {
        const data = res.data || {};
        setPreview(data);
        if (data.first_name) setFirstName(data.first_name);
        if (data.last_name) setLastName(data.last_name);
      })
      .catch((e) => {
        toast.error(e.message || "Invalid invite link");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const res = await acceptWorkspaceInvite({
        token,
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      const user = res.data?.user;
      const jwt = res.data?.token;
      if (jwt && user) {
        setAdminSession(jwt, user);
        toast.success(`Welcome to ${preview?.tenant_name || "your workspace"}!`);
        navigate(ROUTES.dashboard, { replace: true });
      }
    } catch (err) {
      toast.error(err.message || "Could not join workspace");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-login-page admin-login-stack-page admin-login-join-page" data-theme="light">
      <Toaster position="top-center" />
      <main className="admin-login-join-main">
          <form onSubmit={handleSubmit} className="admin-login-card admin-login-stack-card">
            <h2 className="admin-login-title">Join your workspace</h2>
            {loading ? (
              <p className="admin-login-subtitle">Verifying invitation…</p>
            ) : preview ? (
              <p className="admin-login-subtitle">
                You&apos;ve been invited to <strong>{preview.tenant_name}</strong>
                {preview.role_name ? ` as ${preview.role_name}` : ""}. Set your password to continue.
              </p>
            ) : (
              <p className="admin-login-subtitle">
                This invite link is invalid or expired. Ask your workspace admin for a new invitation.
              </p>
            )}

            {preview && !loading ? (
              <>
                <label className="admin-login-label" htmlFor="join-email">
                  Email
                </label>
                <input
                  id="join-email"
                  type="email"
                  className="admin-login-input"
                  value={preview.email || ""}
                  readOnly
                />

                <label className="admin-login-label" htmlFor="join-first">
                  First name
                </label>
                <input
                  id="join-first"
                  type="text"
                  className="admin-login-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />

                <label className="admin-login-label" htmlFor="join-last">
                  Last name
                </label>
                <input
                  id="join-last"
                  type="text"
                  className="admin-login-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />

                <label className="admin-login-label" htmlFor="join-password">
                  Password
                </label>
                <input
                  id="join-password"
                  type="password"
                  className="admin-login-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />

                <label className="admin-login-label" htmlFor="join-confirm">
                  Confirm password
                </label>
                <input
                  id="join-confirm"
                  type="password"
                  className="admin-login-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />

                <button type="submit" className="admin-login-submit" disabled={submitting}>
                  {submitting ? "Joining…" : "Join workspace"}
                </button>
              </>
            ) : null}

            <p className="admin-login-footnote admin-login-footnote--muted">
              Already have access? <Link to={ROUTES.login}>Sign in</Link>
            </p>
          </form>
      </main>
    </div>
  );
}
